import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

const CodeRenderer: React.FC<any> = ({ inline, className, children, ...props }) => {
  const value = String(children).replace(/\n$/, "");
  const match = /language-(\w+)/.exec(className || "");
  if (!inline && match) {
    const lang = match[1];
    return (
      <div className="relative my-3 rounded-lg overflow-hidden border border-gray-700">
        <button
          onClick={() => navigator.clipboard?.writeText(value)}
          className="absolute top-2 right-2 text-xs px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
          style={{ fontSize: '11px' }}
        >
          Copy
        </button>
        <SyntaxHighlighter
          language={lang}
          style={oneDark}
          PreTag="div"
          customStyle={{ 
            margin: 0, 
            padding: "0.875rem", 
            borderRadius: 8,
            fontSize: '13px',
            lineHeight: 1.4
          }}
          {...props}
        >
          {value}
        </SyntaxHighlighter>
      </div>
    );
  }
  return <code className="bg-white/5 px-1 rounded text-xs">{value}</code>;
};

const MarkdownRenderer: React.FC<{ content: string; theme?: any }> = ({ content }) => {
  return (
    // use the custom class "markdown-prose" for tighter control
    <div className="markdown-prose">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          code: CodeRenderer,
          a: ({ href, children, title }) => (
            <a 
              href={href} 
              title={title} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="underline hover:no-underline transition-all text-sm"
            >
              {children}
            </a>
          ),
          img: ({ src, alt, title }) => (
            <img 
              src={src} 
              alt={alt} 
              title={title} 
              className="max-w-full rounded-md mt-2 mb-2 shadow-sm" 
            />
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full text-sm">{children}</table>
            </div>
          ),
          blockquote: ({ children }) => (
            <blockquote className="pl-3 border-l-3 italic my-2 text-sm opacity-90">
              {children}
            </blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;